import { useState } from 'react';
import { addDays, EXCEPTION_LABELS, fmtDate, type BreakKind, type EngineOutputDTO, type RuleTemplateDTO, type RuleTestShiftInput } from '@em/shared';
import { api, useSettings } from '../../api';
import { hrs, mondayOf, todayIn } from '../../components/format';
import { Alert, Button, Card, EmptyState, ErrorAlert, Field, Hours, Input, Select, SeverityBadge } from '../../components/ui';

interface ShiftRow extends RuleTestShiftInput {
  breaks: { start: string; end: string; kind: BreakKind }[];
  rosterStart: string;
  rosterEnd: string;
}

const blankShift = (date: string): ShiftRow => ({ date, start: '07:00', end: '15:30', breaks: [{ start: '12:00', end: '12:30', kind: 'meal' }], siteKind: 'factory', rosterStart: '', rosterEnd: '' });

/** Runs sample shifts through a template's current rules without touching real data. */
export function RuleTester({ templates, defaultTemplateId }: { templates: RuleTemplateDTO[]; defaultTemplateId: string | null }) {
  const settings = useSettings();
  const tz = settings.data?.defaultTimezone ?? 'Australia/Sydney';
  const monday = mondayOf(todayIn(tz));
  const [templateId, setTemplateId] = useState<string>('');
  const [shifts, setShifts] = useState<ShiftRow[]>(() => [0, 1, 2, 3, 4].map((i) => blankShift(addDays(monday, i))));
  const [holidays, setHolidays] = useState('');
  const [result, setResult] = useState<EngineOutputDTO | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const tid = templateId || defaultTemplateId || '';
  const template = templates.find((t) => t.id === tid);
  const config = template?.versions[0]?.config;
  const update = (i: number, p: Partial<ShiftRow>) => setShifts((s) => s.map((x, j) => (j === i ? { ...x, ...p } : x)));

  const run = async () => {
    if (!config) return;
    setBusy(true);
    setError(null);
    try {
      const roster = shifts.filter((s) => s.rosterStart && s.rosterEnd).map((s) => ({ date: s.date, start: s.rosterStart, end: s.rosterEnd }));
      const publicHolidays = holidays
        .split(/[\s,]+/)
        .map((d) => d.trim())
        .filter(Boolean);
      setResult(
        await api.testRules({
          config,
          timezone: tz,
          shifts: shifts.map((s) => ({ date: s.date, start: s.start, end: s.end, siteKind: s.siteKind, breaks: s.breaks.filter((b) => b.start && b.end) })),
          publicHolidays: publicHolidays.length ? publicHolidays : undefined,
          roster: roster.length ? roster : undefined,
        }),
      );
    } catch (e) {
      setError(e);
      setResult(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Rule tester" className="section" actions={<span className="small muted">Uses the current version of the chosen template. Nothing is saved.</span>}>
      <div className="stack">
        <div className="filters" style={{ marginBottom: 0 }}>
          <Field label="Template" className="wide">
            <Select value={tid} onChange={(e) => setTemplateId(e.target.value)}>
              {templates.length === 0 && <option value="">No templates</option>}
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} (v{t.versions[0]?.versionNumber})
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Public holidays" className="wide" hint="Dates as YYYY-MM-DD, comma separated">
            <Input value={holidays} onChange={(e) => setHolidays(e.target.value)} placeholder="2026-12-25" />
          </Field>
        </div>

        <div className="table-wrap">
          <table className="table compact">
            <thead>
              <tr>
                <th>Date</th>
                <th>Start</th>
                <th>End</th>
                <th>Breaks</th>
                <th>Site kind</th>
                <th>Roster (optional)</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shifts.map((s, i) => (
                <tr key={i}>
                  <td>
                    <Input className="sm" type="date" aria-label={`Shift ${i + 1} date`} value={s.date} onChange={(e) => update(i, { date: e.target.value })} />
                    <div className="small muted">{s.date ? fmtDate(s.date) : ''}</div>
                  </td>
                  <td>
                    <Input className="sm" type="time" aria-label={`Shift ${i + 1} start`} value={s.start} onChange={(e) => update(i, { start: e.target.value })} />
                  </td>
                  <td>
                    <Input className="sm" type="time" aria-label={`Shift ${i + 1} end`} value={s.end} onChange={(e) => update(i, { end: e.target.value })} />
                  </td>
                  <td>
                    {s.breaks.map((b, j) => (
                      <div className="row" key={j} style={{ marginBottom: 4, flexWrap: 'nowrap' }}>
                        <Select className="sm" style={{ width: 80 }} aria-label="Break kind" value={b.kind} onChange={(e) => update(i, { breaks: s.breaks.map((x, k) => (k === j ? { ...x, kind: e.target.value as BreakKind } : x)) })}>
                          <option value="meal">Meal</option>
                          <option value="rest">Rest</option>
                        </Select>
                        <Input className="sm" type="time" style={{ width: 100 }} aria-label="Break start" value={b.start} onChange={(e) => update(i, { breaks: s.breaks.map((x, k) => (k === j ? { ...x, start: e.target.value } : x)) })} />
                        <Input className="sm" type="time" style={{ width: 100 }} aria-label="Break end" value={b.end} onChange={(e) => update(i, { breaks: s.breaks.map((x, k) => (k === j ? { ...x, end: e.target.value } : x)) })} />
                        <Button size="sm" variant="ghost" aria-label="Remove break" onClick={() => update(i, { breaks: s.breaks.filter((_, k) => k !== j) })}>
                          ✕
                        </Button>
                      </div>
                    ))}
                    <Button size="sm" variant="ghost" onClick={() => update(i, { breaks: [...s.breaks, { start: '', end: '', kind: 'rest' }] })}>
                      + Break
                    </Button>
                  </td>
                  <td>
                    <Select className="sm" aria-label="Site kind" value={s.siteKind ?? ''} onChange={(e) => update(i, { siteKind: e.target.value || undefined })}>
                      <option value="factory">Factory</option>
                      <option value="install">Install</option>
                      <option value="supplier">Supplier</option>
                      <option value="other">Other</option>
                    </Select>
                  </td>
                  <td>
                    <div className="row" style={{ flexWrap: 'nowrap' }}>
                      <Input className="sm" type="time" style={{ width: 100 }} aria-label="Roster start" value={s.rosterStart} onChange={(e) => update(i, { rosterStart: e.target.value })} />
                      <Input className="sm" type="time" style={{ width: 100 }} aria-label="Roster end" value={s.rosterEnd} onChange={(e) => update(i, { rosterEnd: e.target.value })} />
                    </div>
                  </td>
                  <td className="actions">
                    <Button size="sm" variant="ghost" disabled={shifts.length === 1} onClick={() => setShifts((x) => x.filter((_, j) => j !== i))}>
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="row">
          <Button size="sm" disabled={shifts.length >= 14} onClick={() => setShifts((x) => [...x, blankShift(addDays(x.at(-1)?.date ?? monday, 1))])}>
            + Add shift
          </Button>
          <span className="small muted">{shifts.length} of 14 shifts. An end earlier than the start finishes the next day.</span>
          <span className="spacer" />
          <Button variant="primary" onClick={run} loading={busy} disabled={!config}>
            Run test
          </Button>
        </div>
        <ErrorAlert error={error} />

        {result && (
          <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(240px, 1fr)' }}>
            <div className="stack">
              <h3>Resulting lines</h3>
              {result.lines.length === 0 ? (
                <EmptyState title="No lines produced" />
              ) : (
                <table className="table compact card">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Category</th>
                      <th className="num">Hours / units</th>
                      <th>Rule</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.lines.map((l, i) => (
                      <tr key={i}>
                        <td className="nowrap">{fmtDate(l.date)}</td>
                        <td className="strong">{l.category}</td>
                        <td className="num">{l.unit === 'units' ? `${l.hours} unit${l.hours === 1 ? '' : 's'}` : <Hours value={l.hours} />}</td>
                        <td className="small muted">{l.ruleRef}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <h3>Exceptions</h3>
              {result.exceptions.length === 0 ? (
                <Alert tone="green">No exceptions raised.</Alert>
              ) : (
                <table className="table compact card">
                  <tbody>
                    {result.exceptions.map((x, i) => (
                      <tr key={i}>
                        <td className="nowrap">{fmtDate(x.date)}</td>
                        <td>{EXCEPTION_LABELS[x.type]}</td>
                        <td>
                          <SeverityBadge severity={x.severity} />
                        </td>
                        <td>{x.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div>
              <h3 style={{ marginBottom: 8 }}>Totals</h3>
              <table className="table compact card">
                <tbody>
                  {Object.entries(result.totals.byCategory).map(([c, h]) => (
                    <tr key={c}>
                      <td>{c}</td>
                      <td className="num">{hrs(h)}</td>
                    </tr>
                  ))}
                  <tr className="total">
                    <td>Worked</td>
                    <td className="num">
                      <Hours value={result.totals.workedHours} />
                    </td>
                  </tr>
                  <tr className="total">
                    <td>Paid</td>
                    <td className="num">
                      <Hours value={result.totals.paidHours} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
